import { DateTime } from 'luxon';
import { IAvailableDeliverySlot } from '../../models/edit-delivery-address';

// Get dates from /available-delivery-slots endpoint
export const getDates = (slots: IAvailableDeliverySlot[]) => {
  const toStartOfDay = (input: string | Date) => {
    const date = typeof input === 'string' ? new Date(input) : input;
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  };

  // Treat a day as "available" if it appears in the list (by startDateTime day)
  const availableDayTimes = Array.from(
    new Set(slots.map((slot) => toStartOfDay(slot.startDateTime).getTime())),
  );

  const minTime = Math.min(...availableDayTimes);
  const maxTime = Math.max(...availableDayTimes);
  const minDate = new Date(minTime);
  const maxDate = new Date(maxTime);

  // Build every day in the [minDate..maxDate] range
  const msPerDay = 24 * 60 * 60 * 1000;
  const totalDays = Math.floor((maxTime - minTime) / msPerDay) + 1;
  const allDays = Array.from(
    { length: totalDays },
    (any_, i) =>
      new Date(
        minDate.getFullYear(),
        minDate.getMonth(),
        minDate.getDate() + i,
      ),
  );

  // Unavailable = days not present in the available set
  const availableSet = new Set(availableDayTimes);
  const unavailableDates = allDays.filter(
    (day) => !availableSet.has(day.getTime()),
  );

  return { minDate, maxDate, unavailableDates };
};

export const findSlotIdByDay = (
  slots: IAvailableDeliverySlot[],
  day: Date | string,
): string | null => {
  const startOfDayTime = (value: Date | string) => {
    const date = typeof value === 'string' ? new Date(value) : value;
    return new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
    ).getTime();
  };

  const target = startOfDayTime(day);
  return (
    slots.find((slot) => startOfDayTime(slot.startDateTime) === target)?.id ??
    null
  );
};

// Get dates for /available-fulfillment-options endpoint
export const formatDateForDelivery = (startDate?: string, endDate?: string) => {
  if (!startDate || !endDate) {
    return {
      isSameDay: false,
      fullDate: '',
      startDate: '',
      range: '',
    };
  }

  const start = DateTime.fromISO(startDate);
  const end = DateTime.fromISO(endDate);

  // Validate the parsed dates
  if (!start.isValid || !end.isValid) {
    return {
      isSameDay: false,
      fullDate: 'Invalid date',
      startDate: 'Invalid date',
      range: 'Invalid date range',
    };
  }

  const today = DateTime.local().startOf('day');
  const tomorrow = today.plus({ days: 1 });
  const isSameDay = start.hasSame(end, 'day');

  let fullDate = '';
  if (isSameDay) {
    if (start.hasSame(today, 'day')) {
      fullDate = 'Today';
    } else if (start.hasSame(tomorrow, 'day')) {
      fullDate = 'Tomorrow';
    } else {
      fullDate = start.toFormat('ccc, LLL d');
    }
  }

  const formattedStartDate = start.toFormat('ccc, LLL d');

  // Generate range if the dates are different
  const range = !isSameDay
    ? `${start.toFormat('ccc, LLL d')} - ${end.toFormat('ccc, LLL d')}`
    : '';

  return {
    isSameDay,
    fullDate,
    startDate: formattedStartDate,
    range,
  };
};
